import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setUnauthorizedHandler } from '../services/api';
import apiCache from '../utils/apiCache';

const AuthContext = createContext(null);

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slowStart, setSlowStart] = useState(false);
  useEffect(() => {
    const slowTimer = setTimeout(() => setSlowStart(true), 4000);
    authAPI.me()
      .then((res) => setUser(res.data.user))
      .catch(() => setUser(null))
      .finally(() => { clearTimeout(slowTimer); setLoading(false); });
    return () => clearTimeout(slowTimer);
  }, []);

  // Register 401 handler: clear local state and let <ProtectedRoute>
  // redirect to /login on the next render.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    apiCache.clear();
    setUser(null);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    const { user: userData } = res.data;
    setUser(userData);
    return userData;
  };

  // ── Inactivity auto-logout ──────────────────────────────────────────────────
  // Los eventos solo actualizan una marca de tiempo (barato); un intervalo de
  // 30 s comprueba si se superó INACTIVITY_MS. Peor caso: logout 30 s tarde.
  useEffect(() => {
    if (!user) return;

    let lastActivity = Date.now();
    const onActivity = () => { lastActivity = Date.now(); };

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => document.addEventListener(e, onActivity, { passive: true }));

    const checker = setInterval(() => {
      if (Date.now() - lastActivity >= INACTIVITY_MS) logout();
    }, 30 * 1000);

    return () => {
      clearInterval(checker);
      events.forEach((e) => document.removeEventListener(e, onActivity));
    };
  }, [user, logout]);
  // ───────────────────────────────────────────────────────────────────────────

  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  return (
    <AuthContext.Provider value={{ user, loading, slowStart, login, logout, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
